import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import {
  useQuery,
  useMutation,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  Button,
  useModal,
  PageWrapper,
  useNotification,
  useFilterReducer,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useAuth } from '@sopranodesign/pulse-auth';
import {
  ConditionalProvider,
  ConditionalTypeContext
} from '@sopranodesign/pulse-conditionals';

import SubscribersRouter from './SubscribersRouter';
import SubscriberTable from './SubscriberTable';
import SubscriberFilters from './SubscriberFilters';
import {
  massDelete,
  massExportData,
  massExportTranscript
} from '../../api/subscribers';
import {
  deleteSubscriber as deleteSingleSubscriber,
  exportSubscriber as exportSingleSubscriber,
  exportTranscript as exportSingleTranscript
} from './Pages/Subscriber/api';

const USER_TYPES = {
  REGULAR: { name: 'Regular', value: 'REGULAR', params: { type: 'regular' } },
  DEMO: { name: 'Demo', value: 'DEMO', params: { type: 'demo' } }
};

const DATE_TYPES = {
  LAST_MESSAGE: { name: 'Last message', value: 'last_active_at' },
  CONVERSATION_STARTED: { name: 'Conversation started', value: 'created_at' }
};

const defaultFilters = {
  id: null,
  type: USER_TYPES.REGULAR.value,
  date_type: DATE_TYPES.LAST_MESSAGE.value,
  authorised: null,
  keyword: '',
  start: new Date(dayjs().subtract(1, 'weeks')),
  end: new Date(dayjs().add(1, 'hours')),
  hasConditionals: false,
  conditionals: []
};

export const getTime = (date) => dayjs(date).format('h:mm:ss A');

function SubscribersContent() {
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const pagination = usePaginationReducer({ id: 'subscribers/conversations' });
  const order = useOrderableTableReducer({ id: 'subscribers/conversations' });
  const filters = useFilterReducer({
    id: 'subscribers',
    initialFilters: defaultFilters,
    options: { pagination }
  });

  const { showModal, hideModal } = useModal();
  const { user: loggedInUser } = useAuth();
  const history = useHistory();
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'subscribers/conversations',
      {
        ...filters.form,
        ...USER_TYPES[filters.form.type].params,
        ...pagination.params,
        ...order.params
      }
    ],
    gcTime: 0
  });

  const exportSubscribers = useMutation({
    mutationFn: massExportData,
    onSuccess: () => showSuccess('You will receive an email shortly with details on accessing the subscribers data')
  });

  const exportSubscriber = useMutation({
    mutationFn: exportSingleSubscriber,
    onSuccess: () => showSuccess('You will receive an email shortly with details on accessing the subscribers data')
  });

  const exportTranscripts = useMutation({
    mutationFn: massExportTranscript,
    onSuccess: () => showSuccess(`You will receive an email shortly with details on accessing the subscribers transcript${selectedSubscribers.length > 0 ? 's' : ''}`)
  });

  const exportTranscript = useMutation({
    mutationFn: exportSingleTranscript,
    onSuccess: () => showSuccess('You will receive an email shortly with details on accessing the subscribers transcript')
  });

  const deleteSubscribers = useMutation({
    mutationFn: massDelete,
    onSuccess: async () => {
      await queryClient.invalidateQueries('subscribers/conversations');
      history.push('/conversations');
      showSuccess(`Successfully deleted subscriber${selectedSubscribers.length > 0 ? 's' : ''}`);
    }
  });

  const deleteSubscriber = useMutation({
    mutationFn: deleteSingleSubscriber,
    onSuccess: async () => {
      await queryClient.invalidateQueries('subscribers/conversations');
      history.push('/conversations');
      showSuccess('Successfully deleted subscriber');
    }
  });

  const allItemsSelected = useMemo(() => {
    if (!query.isSuccess) {
      return false;
    }
    const ids = query.data.data.map(({ id }) => id);
    return ids.every((id) => selectedSubscribers.includes(id));
  }, [query.data, query.isSuccess, selectedSubscribers]);

  const toggleSelectAll = () => {
    if (!query.isSuccess) {
      return;
    }
    const ids = query.data.data.map(({ id }) => id);
    setSelectedSubscribers((prev) =>
      allItemsSelected
        ? prev.filter((id) => !ids.includes(id))
        : [...new Set([...prev, ...ids])]
    );
  };

  const confirmAction = ({ header, body, action }) => {
    showModal({
      header,
      body,
      handleConfirm: async () => {
        try {
          await action();
        } finally {
          setSelectedSubscribers([]);
          hideModal();
        }
      }
    });
  };

  const handleExportData = (ids) => {
    const isMulti = Array.isArray(ids);
    confirmAction({
      header: `Export Subscriber${isMulti && ids.length > 1 ? 's' : ''} data`,
      body: `Are you sure you want to export this subscribers data? You will receive an email at "${loggedInUser.email}" with a link to download all data stored on them.`,
      action: () => (isMulti ? exportSubscribers.mutateAsync(ids) : exportSubscriber.mutateAsync(ids))
    });
  };

  const handleExportTranscript = (ids) => {
    const isMulti = Array.isArray(ids);
    confirmAction({
      header: `Export Subscriber${isMulti && ids.length > 1 ? 's' : ''} transcript${isMulti && ids.length > 1 ? 's' : ''}`,
      body: `Are you sure you want to export this subscribers transcript? You will receive an email at "${loggedInUser.email}" with a link to download their conversation transcript.`,
      action: () => (isMulti ? exportTranscripts.mutateAsync(ids) : exportTranscript.mutateAsync(ids))
    });
  };

  const handleDelete = (ids) => {
    const isMulti = Array.isArray(ids);
    confirmAction({
      header: 'Delete',
      body: 'Are you sure you would like to delete all selected subscribers? This will remove all data associated with them, including all of their messages.',
      action: () => (isMulti ? deleteSubscribers.mutateAsync(ids) : deleteSubscriber.mutateAsync(ids))
    });
  };

  return (
    <PageWrapper
      footer={selectedSubscribers.length > 0}
      header="Conversations"
      subheader="View all chat transcripts"
      actions={
        <>
          <Button colour="primary" icon="refresh" loading={query.isLoading} mr onClick={() => query.refetch()} variant="secondary">Refresh</Button>
          <SubscriberFilters filters={filters} query={query} />
        </>
      }
    >
      <SubscriberTable
        query={query}
        order={order}
        pagination={pagination}
        getTime={getTime}
        selectedSubscribers={selectedSubscribers}
        allItemsSelected={allItemsSelected}
        handleSelectingAllItems={toggleSelectAll}
        handleSubscriberSelect={(id) => {
          setSelectedSubscribers((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
          );
        }}
        handleExportData={handleExportData}
        handleExportTranscript={handleExportTranscript}
        handleDelete={handleDelete}
      />
    </PageWrapper>
  );
}

const ConditionalTypeProvider = ({ children }) => {
  const { data } = useQuery({
    queryKey: ['conversations/builder/conditionals'],
  });

  const conditionalTypes =
    data?.data?.filter((content) =>
      ['message-type', 'message-check', 'message-number-check', 'message-group-check'].includes(content.type)
    ) || [];

  return (
    <ConditionalTypeContext.Provider value={conditionalTypes}>
      {children}
    </ConditionalTypeContext.Provider>
  );
};

export default function Subscribers() {
  const subject = { id: '', type: 'message' };
  const newConditional = {
    subject,
    toApi: () => subject
  };

  return (
    <ConditionalTypeProvider>
      <ConditionalProvider subjects={[newConditional]}>
        <SubscribersRouter />
        <SubscribersContent />
      </ConditionalProvider>
    </ConditionalTypeProvider>
  );
}